<template>
  <div
    ref="menuDrawer"
    class="menu-drawer critical-component-hide"
    :class="[
      `show-tier${currentTier}`,
      { [$class('loading')]: state === 'loading' },
    ]"
    js-overlay="scrollElement"
  >
    <div
      v-if="currentTier > 1"
      class="menu-drawer__back-buttons"
    >
      <button
        v-for="(link, index) of currentLinks"
        :key="`back-to-${link.handle}`"
        class="
          menu-drawer__link
          menu-drawer__button
          menu-drawer__back
        "
        :tabindex="0"
        type="button"
        @click="handleBackClick(index)"
      >
        <icon-chevron-left />

        <span class="menu-drawer__back-text">
          <span
            class="text-body-xs-regular"
            v-text="$string('general.menu_drawer.back_to')"
          />

          <span
            class="text-body-xs-bold"
            v-text="
              index === 0
                ? $string('general.menu_drawer.title')
                : currentLinks[index - 1].title
            "
          />
        </span>
      </button>
    </div>

    <div class="menu-drawer__navigation">
      <nav
        ref="menus"
        class="menu-drawer__menus"
        :style="{ height: state === 'loading' ? '100vh' : null }"
      >
        <!-- Tier 1 container -->
        <ul
          class="menu-drawer__menu menu-drawer__tier1"
          :class="{ [$class('active')]: getActiveState(false) }"
        >
          <!-- Tier 1 title -->
          <li class="menu-drawer__menu-item">
            <h2
              id="overlay-menuDrawer"
              :ref="(args) => setItemRefs(args, {
                tier: 1,
                type: 'heading',
              })"
              class="menu-drawer__heading text-heading-2xl"
              v-html="getTier1LinkText(false)"
            />
          </li>

          <!-- Tier 1 links -->
          <template
            v-for="(tier1) of navigation"
            :key="tier1.handle"
          >
            <li class="menu-drawer__menu-item">
              <div class="menu-drawer__link-container">
                <a
                  :ref="(args) => setItemRefs(args, {
                    tier: 1,
                    type: 'link',
                  })"
                  class="menu-drawer__link text-body-m-regular"
                  :class="{ 'menu-drawer__cta': isValidCta(tier1.title) }"
                  :href="tier1.url"
                  :tabindex="getActiveState(false) ? 0 : -1"
                  :style="{ color: tier1.color }"
                >
                  <span v-text="tier1.title" />
                </a>

                <button
                  v-if="tier1.links.length || tier1.promotions.length"
                  :ref="(args) => setItemRefs(args, {
                    ignoreHeight: true,
                    linksTo: tier1.handle,
                    tier: 1,
                    type: 'link',
                  })"
                  class="
                    menu-drawer__link-button
                    menu-drawer__button
                    text-body-m-regular
                  "
                  :tabindex="getActiveState(false) ? 0 : -1"
                  type="button"
                  @click="handleLinkClick(tier1)"
                >
                  <span
                    class="visually-hidden"
                    v-text="$string('accessibility.open')"
                  />

                  <icon-chevron-right />
                </button>
              </div>

              <!-- Tier 2 container -->
              <ul
                v-if="tier1.links.length || tier1.promotions.length"
                class="menu-drawer__menu menu-drawer__tier2"
                :class="{ [$class('active')]: getActiveState(tier1) || getChildActiveState(tier1) }"
              >
                <!-- Tier 2 title -->
                <li class="menu-drawer__menu-item">
                  <h2
                    :ref="(args) => setItemRefs(args, {
                      handle: tier1.handle,
                      tier: 2,
                      type: 'heading',
                    })"
                    class="menu-drawer__heading text-heading-2xl"
                    v-text="tier1.title"
                  />
                </li>

                <!-- Tier 2 search -->
                <div
                  :ref="(args) => setItemRefs(args, {
                    handle: tier1.handle,
                    tier: 2,
                    type: 'search',
                  })"
                  class="menu-drawer__search-bar-container"
                >
                  <search-bar
                    namespace="menu-drawer"
                    :search-query="searchQueryTier2"
                    class="menu-drawer__search-bar"
                    :placeholder="$string('general.menu_drawer.search.tier2_placeholder')"
                    @search-input="searchQueryTier2 = $event"
                    @clear="searchQueryTier2 = ''"
                  />
                </div>

                <!-- Tier 2 links -->
                <template
                  v-for="(tier2) of filterTierLinks(tier1, searchQueryTier2)"
                  :key="tier2.handle"
                >
                  <li class="menu-drawer__menu-item">
                    <div class="menu-drawer__link-container">
                      <a
                        :ref="(args) => setItemRefs(args, {
                          handle: tier1.handle,
                          tier: 2,
                          type: 'link',
                        })"
                        class="menu-drawer__link text-body-m-regular"
                        :class="{ 'menu-drawer__cta': isValidCta(tier2.title) }"
                        :href="tier2.url"
                        :style="{ color: tier2.color }"
                        :tabindex="getActiveState(tier1) ? 0 : -1"
                      >
                        <span v-text="getLinkText(tier2.title)" />
                      </a>

                      <button
                        v-if="tier2.links.length || tier2.promotions.length"
                        :ref="(args) => setItemRefs(args, {
                          handle: tier1.handle,
                          ignoreHeight: true,
                          linksTo: tier2.handle,
                          tier: 2,
                          type: 'link',
                        })"
                        class="
                          menu-drawer__link-button
                          menu-drawer__button
                          text-body-m-regular
                        "
                        :tabindex="getActiveState(tier1) ? 0 : -1"
                        type="button"
                        @click="handleLinkClick(tier2)"
                      >
                        <span
                          class="visually-hidden"
                          v-text="$string('accessibility.open')"
                        />

                        <icon-chevron-right />
                      </button>
                    </div>

                    <!-- Tier 3 container -->
                    <ul
                      v-if="tier2.links.length || tier2.promotions.length"
                      class="menu-drawer__menu menu-drawer__tier3"
                      :class="{
                        [$class('active')]: getActiveState(tier2) || getChildActiveState(tier2)
                      }"
                    >
                      <!-- Tier 3 title -->
                      <li class="menu-drawer__menu-item">
                        <h2
                          :ref="(args) => setItemRefs(args, {
                            handle: tier2.handle,
                            tier: 3,
                            type: 'heading',
                          })"
                          class="menu-drawer__heading text-heading-2xl"
                          v-text="tier2.title"
                        />
                      </li>

                      <!-- Tier 3 search -->
                      <div
                        :ref="(args) => setItemRefs(args, {
                          handle: tier2.handle,
                          tier: 3,
                          type: 'search',
                        })"
                        class="menu-drawer__search-bar-container"
                      >
                        <search-bar
                          :ref="(args) => setItemRefs(args, {
                            handle: tier2.handle,
                            tier: 3,
                            type: 'search',
                          })"
                          namespace="menu-drawer"
                          :search-query="searchQueryTier3"
                          class="menu-drawer__search-bar"
                          :placeholder="$string('general.menu_drawer.search.tier3_placeholder')"
                          @search-input="searchQueryTier3 = $event"
                          @clear="searchQueryTier3 = ''"
                        />
                      </div>

                      <!-- Tier 3 links -->
                      <template
                        v-for="(tier3) of filterTierLinks(tier2, searchQueryTier3)"
                        :key="tier3.handle"
                      >
                        <li class="menu-drawer__menu-item">
                          <button
                            v-if="tier3.links.length"
                            :ref="(args) => setItemRefs(args, {
                              handle: tier2.handle,
                              linksTo: tier3.handle,
                              tier: 3,
                              type: 'link',
                            })"
                            class="
                              menu-drawer__link
                              menu-drawer__button
                              text-body-m-regular
                            "
                            :tabindex="getActiveState(tier2) ? 0 : -1"
                            type="button"
                            @click="handleLinkClick(tier3)"
                          >
                            <span
                              class="visually-hidden"
                              v-text="$string('accessibility.open')"
                            />

                            <span
                              :style="{ color: tier3.color }"
                              v-text="tier3.title"
                            />

                            <icon-chevron-right />
                          </button>

                          <a
                            v-else
                            :ref="(args) => setItemRefs(args, {
                              handle: tier2.handle,
                              tier: 3,
                              type: 'link',
                            })"
                            class="
                              menu-drawer__link
                              text-body-m-regular
                            "
                            :class="{ 'menu-drawer__cta': isValidCta(tier3.title) }"
                            :href="tier3.url"
                            :style="{ color: tier3.color }"
                            :tabindex="getActiveState(tier2) ? 0 : -1"
                            v-text="getLinkText(tier3.title)"
                          />

                          <!-- Tier 4 container -->
                          <ul
                            v-if="tier3.links.length"
                            class="menu-drawer__menu menu-drawer__tier4"
                            :class="{ [$class('active')]: getActiveState(tier3) }"
                          >
                            <!-- Tier 4 title -->
                            <li class="menu-drawer__menu-item">
                              <h2
                                :ref="(args) => setItemRefs(args, {
                                  handle: tier3.handle,
                                  tier: 4,
                                  type: 'heading',
                                })"
                                class="menu-drawer__heading text-heading-2xl"
                                v-text="tier3.title"
                              />
                            </li>

                            <!-- Tier 4 links -->
                            <template
                              v-for="(tier4) of tier3.links"
                              :key="tier4.handle"
                            >
                              <li class="menu-drawer__menu-item">
                                <a
                                  :ref="(args) => setItemRefs(args, {
                                    handle: tier3.handle,
                                    tier: 4,
                                    type: 'link',
                                  })"
                                  class="
                                    menu-drawer__link
                                    text-body-m-regular
                                  "
                                  :class="{ 'menu-drawer__cta': isValidCta(tier4.title) }"
                                  :href="tier4.url"
                                  :tabindex="getActiveState(tier3) ? 0 : -1"
                                  v-text="getLinkText(tier4.title)"
                                />
                              </li>
                            </template>
                          </ul>
                        </li>
                      </template>

                      <!-- Tier 3 promotions -->
                      <li
                        v-if="tier2.promotions.length"
                        :ref="(args) => setItemRefs(args, {
                          handle: tier2.handle,
                          tier: 3,
                          type: 'promotions',
                        })"
                        class="menu-drawer__promotions"
                        :class="{ 'top-margin': tier2.links.length }"
                      >
                        <div
                          v-for="(promotion) of tier2.promotions"
                          :key="promotion.handle"
                          class="menu-drawer__promotion-container"
                        >
                          <a
                            class="menu-drawer__promotion"
                            :href="promotion.url"
                            :tabindex="getActiveState(tier2) ? 0 : -1"
                          >
                            <responsive-image
                              class="menu-drawer__promotion-image"
                              :image="{ src: promotion.image }"
                              ratio="2.6:1"
                              sizes="100vw"
                            />

                            <div class="menu-drawer__promotion-overlay" />

                            <p
                              class="
                                menu-drawer__promotion-title
                                text-body-xs-bold
                              "
                            >
                              <span v-text="promotion.title" />
                              <icon-chevron-right />
                            </p>
                          </a>
                        </div>
                      </li>
                    </ul>
                  </li>
                </template>

                <!-- Tier 2 promotions -->
                <li
                  v-if="tier1.promotions.length"
                  :ref="(args) => setItemRefs(args, {
                    handle: tier1.handle,
                    tier: 2,
                    type: 'promotions',
                  })"
                  class="menu-drawer__promotions"
                  :class="{ 'top-margin': tier1.links.length }"
                >
                  <div
                    v-for="(promotion) of tier1.promotions"
                    :key="promotion.handle"
                    class="menu-drawer__promotion-container"
                  >
                    <a
                      class="menu-drawer__promotion"
                      :href="promotion.url"
                      :tabindex="getActiveState(tier1) ? 0 : -1"
                    >
                      <responsive-image
                        class="menu-drawer__promotion-image"
                        :image="{ src: promotion.image }"
                        ratio="2.6:1"
                        sizes="100vw"
                      />

                      <div class="menu-drawer__promotion-overlay" />

                      <p
                        class="
                          menu-drawer__promotion-title
                          text-body-xs-bold
                        "
                      >
                        <span v-text="promotion.title" />
                        <icon-chevron-right />
                      </p>
                    </a>
                  </div>
                </li>
              </ul>
            </li>
          </template>

          <!-- Account links -->
          <li
            v-if="$variable('customer.enabled')"
            :ref="(args) => setItemRefs(args, { tier: 1, type: 'link' })"
            class="menu-drawer__menu-item menu-drawer__utils"
          >
            <a
              class="menu-drawer__utils-link"
              :href="$variable('customer.loggedIn')
                ? $variable('routes.account.url')
                : $variable('routes.account.login')
              "
              :tabindex="getActiveState(false) ? 0 : -1"
            >
              <span class="menu-drawer__utils-key text-body-xs-bold">
                <icon-account />
                <span v-text="$string('customers.header.my_account')" />
              </span>

              <span
                v-if="!$variable('customer.loggedIn')"
                class="menu-drawer__utils-value text-body-xs-regular"
                v-text="$string('customers.header.sign_in')"
              />
            </a>

            <button
              class="menu-drawer__utils-link"
              :aria-expanded="activeOverlayCountrySwitcher"
              aria-haspopup="dialog"
              :tabindex="getActiveState(false) ? 0 : -1"
              type="button"
              @click="handleCountySwitcher"
            >
              <span class="menu-drawer__utils-key text-body-xs-bold">
                <!-- eslint-disable-next-line -->
                <img
                  v-if="countryFlag"
                  :key="`flag-${$variable('store.country')}`"
                  class="menu-drawer__utils-flag"
                  alt=""
                  role="presentation"
                  :src="countryFlag.src"
                  :srcset="countryFlag.srcset"
                >
                <span v-text="$string('general.site_header.country')" />
              </span>

              <span
                class="menu-drawer__utils-value text-body-xs-regular"
                v-text="$variable('store.countryName')"
              />
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Menu drawer (menu-drawer)
 * -----------------------------------------------------------------------------
 * Mobile menu drawer.
 * - CSS variable --menu-drawer-links-height is updated to match height of
 *   current visible links.
 *
 * @param {String} handle - Mobile linklist handle.
 * @param {Object} overlay - Overlay API.
 * @param {Number} itemCount - Number of tier 1 items in linklist.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'
import SearchBar from '~global/search-bar/search-bar'

import IconAccount from '~icons/general/account.svg'
import IconChevronLeft from '~icons/directional-navigation/chevron/left.svg'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'

import { specialLinks } from '~/config/navigations'

import menuQuery from '~graphql/menu.gql'
import { formatMenu, formatMenuWithCols } from '~/format/format-menu'

export default {
  name: 'MenuDrawer',

  components: {
    IconAccount,
    IconChevronLeft,
    IconChevronRight,
    ResponsiveImage,
    SearchBar,
  },

  props: {
    handle: {
      type: String,
      default: 'main-menu',
    },
    itemCount: {
      type: Number,
      default: 5,
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    parentCollectionUrl: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      currentLink: false,
      currentLinks: [],
      currentHeight: false,
      itemRefs: [],
      navigation: [...Array(this.itemCount)].map((_, index) => {
        return {
          color: '',
          handle: `placeholder-${index}`,
          links: [],
          promotions: [],
          title: '',
          url: this.$variable('routes.root'),
        }
      }),
      searchQueryTier2: '',
      searchQueryTier3: '',
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if multi-country switcher overlay is active.
     * @returns {Boolean}
     */
    activeOverlayCountrySwitcher() {
      return this.getActiveOverlay?.namespace === 'multiCountrySwitcher'
    },

    /**
     * Compute flag URL.
     * @returns {Object}
     */
    countryFlag() {
      const code = this.$variable('store.country').toLowerCase()

      if (!code) {
        return false
      }

      return {
        src: `https://flagcdn.com/w40/${code}.png`,
        srcset: `https://flagcdn.com/w40/${code}.png 40w, https://flagcdn.com/w80/${code}.png 80w, https://flagcdn.com/w160/${code}.png 160w`,
      }
    },

    /**
     * Compute current item refs in current link.
     * @returns {Array}
     */
    currentItemRefs() {
      if (this.currentLink) {
        return this.itemRefs.filter((itemRef) => {
          return itemRef.handle === this.currentLink.handle
        })
      }

      return this.itemRefs.filter((itemRef) => itemRef.tier === 1)
    },

    /**
     * Compute current tier to show.
     * @returns {Number}
     */
    currentTier() {
      if (!this.currentLink) {
        return 1
      }

      return this.currentLink.tier + 1
    },

    /**
     * Compute current back to button.
     * @returns {String}
     */
    currentBackTo() {
      if (this.currentTier === 2) {
        return this.$string('general.menu_drawer.title')
      }

      return this.currentLinks[this.currentLinks.length - 2].title
    },
  },

  watch: {

    /**
     * Watch current link to update focus.
     * - Also clears search bars.
     */
    currentLink(newCurrentLink, oldCurrentLink) {
      if (!this.overlay.isActive) {
        return
      }

      const link = this.itemRefs.find((item) => {
        if (!oldCurrentLink || newCurrentLink.tier > oldCurrentLink.tier) {
          return item.handle === newCurrentLink.handle
        }

        return item.linksTo === oldCurrentLink.handle && item.type === 'link'
      })

      this.searchQueryTier2 = ''
      this.searchQueryTier3 = ''
      this.overlay.update({ currentElement: link?.element })
      this.updateHeight()
      this.$refs.menuDrawer.scroll(0, 0)
    },

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay('menuDrawer')
    },

    /**
     * Watch overlay is active to update height and scroll position on open.
     * - Also resets menu position on close.
     */
    'overlay.isActive'() {
      if (!this.overlay.isActive) {
        this.handleBackClick(0)
        return
      }

      this.openMenu()
    },
  },

  async mounted() {
    if (!this.handle) {
      return
    }

    const start = performance.now()
    await this.queryMenu()
    this.openMenu()
    await this.handleQueryFinish(start)
  },

  beforeUpdate() {
    this.itemRefs = []
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Open menu logic.
     * - Navigate to parent collection if on product.
     * - Otherwise fallback to existing PLP logic.
     */
    openMenu() {
      if (this.parentCollectionUrl) {
        this.navigateToParentCollection(this.parentCollectionUrl)
        return
      }

      this.navigateToCurrentPath()
    },

    /**
     * Filter tier links for a specific tier item.
     * @param {Object} tier - The tier item containing links to filter.
     * @param {string} searchQuery - The search query to filter links by title.
     * @returns {Array}
     */
    filterTierLinks(tier, searchQuery) {
      if (!tier || !tier.links) {
        return []
      }

      if (!searchQuery) {
        return tier.links
      }

      const query = searchQuery.toLowerCase()
      return tier.links.filter((item) => item?.title?.toLowerCase()?.includes(query))
    },

    /**
     * Get tier 1 link text or loading placeholder based on state.
     * @param {Object|Boolean} tier - Tier 1 object.
     * @returns {String}
     */
    getTier1LinkText(tier) {
      if (this.state === 'loading') {
        return `<span class="menu-drawer__loading"></span>`
      }

      return tier ? this.getLinkText(tier.title) : this.$string('general.menu_drawer.title')
    },

    /**
     * Get parsed link title based on current title.
     * @param {String} title - Link string
     * @returns {String}
     */
    getLinkText(title) {
      if (!title.includes(specialLinks.showCta)) {
        return title
      }

      const ctaString = title.replace(specialLinks.showCta, '')

      return ctaString ? ctaString : this.$string('general.site_header.view_all')
    },

    /**
     * Get active state for nested menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getActiveState(link) {
      return link.handle === this.currentLink.handle
    },

    /**
     * Get active state of current nested menu's child menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getChildActiveState(link) {
      const tier3LinkActive = link.links.find((tier3Link) => {
        const tier4LinkActive = tier3Link.links.find((tier4Link) => {
          return tier4Link.handle === this.currentLink.handle
        })
        return tier3Link.handle === this.currentLink.handle || Boolean(tier4LinkActive)
      })

      return Boolean(tier3LinkActive)
    },

    /**
     * Recursively searches navigation items for a matching URL.
     * @param {Array} items - Array of navigation items.
     * @param {String} searchUrl - URL to match.
     * @param {Array} [path=[]] - Accumulated path to current item.
     * @param {Function} setPath - Callback to set the matching path.
     * @returns {Boolean}
     */
    searchNavigation(items, searchUrl, path = [], setPath) {
      for (const item of items) {
        if (!item || !item.url) {
          continue
        }

        const itemUrl = item.url.replace(/^https?:/, '')
        const newPath = [...path, item]

        if (itemUrl === searchUrl) {
          setPath(newPath)
          return true
        }

        if (
          item.links?.length &&
          this.searchNavigation(item.links, searchUrl, newPath, setPath)
        ) {
          return true
        }
      }

      return false
    },

    /**
     * Set item refs.
     * @param {HTMLElement} element - Element in v-for.
     * @param {String} data.handle - Handle of item.
     * @param {Boolean} [data.ignoreHeight] - Ignore element when calculating
     * height of menu.
     * @param {String} [data.linksTo] - What button links to.
     * @param {Number} data.tier - Tier of item.
     * @param {String} data.type - Type of item, `heading`, `search`, `link`.
     */
    setItemRefs(element, data) {
      if (!element) {
        return
      }

      this.itemRefs.push({
        ...data,
        element,
      })
    },

    /**
     * Loads menu from GraphQL.
     * @returns {Promise}
     */
    queryMenu() {
      return new Promise(async(resolve, reject) => {
        try {
          const response = await cnvs.Query({
            query: menuQuery,
            variables: {
              country: this.$variable('store.country'),
              handle: this.handle,
              language: this.$variable('store.language'),
            },
          })

          if (this.$variable('settings.disableComponentReadyState')) {
            resolve()
            return
          }

          const navigation = formatMenu({ response })
          this.navigation = formatMenuWithCols({ items: navigation })
          this.state = 'ready'
          resolve()

        } catch (error) {
          cnvs.ReportError('Failed to load menu', error)
          reject(error)
        }
      })
    },

    /**
     * Find matching navigation path by URL.
     * - If no URL is provided, uses current window location.
     * @param {String} [url] - URL to match.
     * @returns {Array|null}
     */
    findMatchingNavigationPath(url) {
      const searchUrl = url
        ? url.replace(/^https?:/, '')
        : window.location.href.replace(/^https?:/, '')

      let matchingPath = []

      this.searchNavigation(this.navigation, searchUrl, [], (path) => {
        matchingPath = path
      })

      return matchingPath.length ? matchingPath : null
    },

    /**
     * Auto-navigate menu drawer based on current URL.
     */
    navigateToCurrentPath() {
      if (this.state !== 'ready') {
        return
      }

      const matchingPath = this.findMatchingNavigationPath()

      if (!matchingPath) {
        return
      }

      // The last item in the path is the current item
      const targetItem = matchingPath[matchingPath.length - 1]

      // Set up parent path (excluding the target item)
      const parentPath = matchingPath.slice(0, -1)

      // Update current links and link to reflect the found path
      this.currentLinks = parentPath
      this.currentLink = parentPath.length > 0 ? parentPath[parentPath.length - 1] : false

      // If target is not last and has children, navigate into it
      if (targetItem.links && targetItem.links.length) {
        this.handleLinkClick(targetItem)
      }

      // Update the UI
      this.updateHeight()
    },

    /**
     * Navigate to parent collection menu item if data attribute is present.
     * @param {String} parentCollectionUrl - Parent collection URL.
     */
    navigateToParentCollection(parentCollectionUrl) {
      if (!parentCollectionUrl) {
        return
      }

      // Build normalized full URL
      const domain = window.location.origin.replace(/^https?:/, '')
      const fullUrl = `${domain}${parentCollectionUrl}`

      const matchingPath = this.findMatchingNavigationPath(fullUrl)

      if (!matchingPath) {
        return
      }

      if (matchingPath.length === 1) {
        this.currentLinks = [matchingPath[0]]
        this.currentLink = matchingPath[0]
        this.handleLinkClick(matchingPath[0])

      } else {
        this.currentLinks = []
        this.currentLink = false
        matchingPath.slice(0, -1).forEach(link => this.handleLinkClick(link))
      }

      this.updateHeight()
    },

    /**
     * Handles functions to run after the query has finished and menu updated.
     * @param {Number} start - Start time.
     */
    async handleQueryFinish(start) {
      this.removeStaticMenu()
      this.overlay.update()
      const end = performance.now()

      /**
       * Drawer animation must finish before calculating heights.
       * - Wait minimum amount of time based on how long querying the menu took.
       */
      const waitTime = Math.abs(this.$timing('normal') - (end - start))

      await new Promise((resolve) => {
        setTimeout(resolve, waitTime)
      })

      this.updateHeight()
    },

    /**
     * Removes static menu drawer rendered by menu-drawer.snippet.liquid.
     * - Otherwise there would be duplicate menus.
     */
    removeStaticMenu() {
      const container = document.querySelector('[js-menu-drawer="container"]')

      if (!container) {
        return
      }

      container.remove()
    },

    /**
     * Update height of current active navigation.
     * - Goes through each item in currentItemRefs and get height of element.
     */
    async updateHeight() {
      await this.$nextTick()
      let height = 0

      this.currentItemRefs.forEach((ref) => {
        if (!ref?.element?.offsetHeight || ref?.ignoreHeight) {
          return
        }

        height += ref.element.offsetHeight
      })

      /**
       * If height is 0 then re-run function as it's a bug.
       */
      if (height === 0 && this.currentItemRefs.length) {
        setTimeout(() => {
          this.updateHeight()
        }, 100)

        return
      }

      document.documentElement.style.setProperty(
        '--menu-drawer-links-height',
        `${height}px`,
      )

      /**
       * Update Vue data for easier debugging.
       */
      this.currentHeight = height

      /**
       * Scroll to top.
       */
      this.$refs.menuDrawer.scroll(0, 0)

      /**
       * If min-height of menus exceeds height then promotion margin top auto
       * calculation is incorrect.
       * - Set margin
       */
      await this.$nextTick()
      const menusHeight = this.$refs.menus.offsetHeight
      let marginTop = 'auto'

      if (height <= menusHeight) {
        marginTop = `${menusHeight - height}px`
      }

      document.documentElement.style.setProperty(
        '--menu-drawer-auto-margin',
        marginTop,
      )
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     */
    handleLinkClick(link) {
      this.currentLink = link
      this.currentLinks.push(link)
    },

    /**
     * Handle back to parent link.
     */
    handleBackClick(index) {
      if (index === 0) {
        // Go back to top level
        this.currentLinks = []
        this.currentLink = false
        return
      }

      // Remove all links after the target index
      this.currentLinks = this.currentLinks.slice(0, index)
      // Set the current link to the new last link in the array
      this.currentLink = this.currentLinks[index - 1]
    },

    /**
     * Handle country switcher.
     */
    handleCountySwitcher() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'multiCountrySwitcher',
      })
    },

    /**
     * Check if link is valid cta.
     * @param {String} title - Title.
     * @returns {Boolean}
     */
    isValidCta(title) {
      return title.includes(specialLinks.showCta)
    },
  },
}
</script>

<style lang="scss">
@import './menu-drawer';
</style>
